import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ReviewErrorBoundary } from "@/components/ReviewErrorBoundary";
import {
    ArrowRight, ArrowUpRight, BookOpen, Check, Loader2, MoreVertical, RotateCcw,
    Sparkles, AlertCircle, ChevronDown, ChevronUp, Clock, ArrowLeft, User,
    Layers, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/Skeleton";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ScoreGauge } from "@/components/review/ScoreGauge";
import { ScoreBreakdownCard } from "@/components/review/ScoreBreakdownCard";
import { lookupBrushUp } from "@/lib/learnMapping";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 60_000;

export default function PracticeReviewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isPublic = searchParams.get("public") === "1";

    const [session, setSession] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [polling, setPolling] = useState(false);
    const [pollExpired, setPollExpired] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const transcriptRef = useRef(null);
    const [highlightTurn, setHighlightTurn] = useState(null);

    useDocumentTitle(
        session?.avatar_name
            ? `Review — ${session.avatar_name}${session.therapy_type_name ? " — " + session.therapy_type_name : ""}`
            : "Review"
    );

    const load = useCallback(async () => {
        try {
            const path = isPublic
                ? `/sessions/${id}/public`
                : `/sessions/${id}`;
            const res = await api.get(path);
            setSession(res.data);
            return res.data;
        } catch (err) {
            if (err?.response?.status === 404) setFetchError("Review not found");
            return null;
        }
    }, [id, isPublic]);

    useEffect(() => { load(); }, [load]);

    // Poll while review is pending (max 60s total).
    useEffect(() => {
        const rs = session?.review?.status;
        if (rs !== "pending") {
            setPolling(false);
            setPollExpired(false);
            return undefined;
        }
        setPolling(true);
        setPollExpired(false);
        const start = Date.now();
        const handle = setInterval(async () => {
            const s = await load();
            if (s?.review?.status !== "pending") {
                clearInterval(handle);
                setPolling(false);
            } else if (Date.now() - start > POLL_MAX_MS) {
                clearInterval(handle);
                setPolling(false);
                setPollExpired(true);
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(handle);
    }, [session?.review?.status, load]);

    const handleRegenerate = useCallback(async () => {
        setRegenerating(true);
        try {
            const res = await api.post(`/sessions/${id}/review/regenerate`);
            setSession(res.data);
            toast.success("Review regenerated");
        } catch (err) {
            // Server already persisted the failure state; refetch + surface the error.
            await load();
            const raw = err?.response?.data?.detail;
            let msg = "Regeneration failed";
            if (typeof raw === "string") msg = raw;
            else if (raw && typeof raw === "object") msg = raw.message || raw.error || msg;
            toast.error(msg);
        } finally {
            setRegenerating(false);
        }
    }, [id, load]);

    const [continuing, setContinuing] = useState(false);
    const handleContinue = useCallback(async () => {
        setContinuing(true);
        try {
            const res = await api.post(`/sessions/${id}/continue`, {});
            toast.success(`Session ${res.data.session_number_in_series} of 8 — let's go.`);
            navigate(`/practice/session/${res.data.id}`);
        } catch (err) {
            const raw = err?.response?.data?.detail;
            let msg = "Could not start the next session";
            if (typeof raw === "string") msg = raw;
            else if (raw && typeof raw === "object") msg = raw.message || raw.error || msg;
            toast.error(msg);
            setContinuing(false);
        }
    }, [id, navigate]);

    const jumpToTurn = useCallback((turnIndex) => {
        const root = transcriptRef.current;
        if (!root) return;
        const turn = root.querySelector(`[data-turn-idx="${turnIndex}"]`);
        if (!turn) return;
        turn.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightTurn(turnIndex);
        setTimeout(() => setHighlightTurn(null), 2000);
    }, []);

    if (fetchError) {
        return (
            <Layout>
                <div className="mx-auto max-w-xl py-12 text-center" data-testid="review-error">
                    <h1 className="text-2xl font-semibold text-foreground">{fetchError}</h1>
                    <Button asChild variant="outline" className="mt-6"><Link to="/">Back to home</Link></Button>
                </div>
            </Layout>
        );
    }

    if (!session) {
        return (
            <Layout>
                <div className="space-y-6" data-testid="review-loading">
                    <Skeleton className="h-32 w-full rounded-2xl" />
                    <Skeleton className="h-24 w-full rounded-2xl" />
                    <Skeleton className="h-48 w-full rounded-2xl" />
                </div>
            </Layout>
        );
    }

    const review = session.review || {};
    const status = review.status;

    // Skipped — shouldn't normally route here, but show a friendly fallback.
    if (status === "skipped_too_short") {
        return (
            <Layout>
                <div className="mx-auto max-w-xl py-12 text-center space-y-4" data-testid="review-skipped">
                    <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
                    <h1 className="text-2xl font-semibold text-foreground">No review for this session</h1>
                    <p className="text-sm text-muted-foreground">The session ended before there was enough conversation to review meaningfully.</p>
                    <div className="flex justify-center gap-2 pt-2">
                        <Button asChild><Link to="/practice/setup">Practice again</Link></Button>
                        <Button asChild variant="outline"><Link to="/">Back to home</Link></Button>
                    </div>
                </div>
            </Layout>
        );
    }

    if (status === "pending" || polling) {
        return (
            <Layout>
                <div className="mx-auto max-w-xl py-16 text-center space-y-4" data-testid="review-pending">
                    <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                    <h1 className="text-2xl font-semibold text-foreground">Generating supervisor review…</h1>
                    <p className="text-sm text-muted-foreground">This usually takes 10–15 seconds.</p>
                    {pollExpired && (
                        <Button onClick={load} variant="outline" data-testid="review-poll-manual-refresh">
                            <RotateCcw className="h-4 w-4" />
                            Taking longer than usual — refresh
                        </Button>
                    )}
                </div>
            </Layout>
        );
    }

    if (status === "failed") {
        return (
            <Layout>
                <div className="mx-auto max-w-xl py-12 text-center space-y-4" data-testid="review-failed">
                    <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
                    <h1 className="text-2xl font-semibold text-foreground">Review couldn&apos;t be generated</h1>
                    <p className="break-words text-sm text-muted-foreground">
                        {review.error || "The supervisor LLM didn't return a usable response."}
                    </p>
                    <p className="text-xs text-muted-foreground">Attempts: {review.attempts ?? 1}</p>
                    <div className="flex justify-center gap-2 pt-2">
                        <Button onClick={handleRegenerate} disabled={regenerating} data-testid="review-regenerate-button">
                            {regenerating ? (<><Loader2 className="h-4 w-4 animate-spin" />Retrying…</>) : (<><RotateCcw className="h-4 w-4" />Regenerate</>)}
                        </Button>
                        <Button asChild variant="outline"><Link to="/">Back to home</Link></Button>
                    </div>
                </div>
            </Layout>
        );
    }

    // === COMPLETE — full review UI (wrapped in error boundary so any
    // single-card render error shows a friendly fallback instead of a blank
    // screen) ===
    return (
        <Layout>
            <ReviewErrorBoundary>
                <div className="mx-auto max-w-5xl space-y-6 animate-fade-in-up" data-testid="practice-review-page">
                <HeaderCard
                    session={session}
                    review={review}
                    onRegenerate={handleRegenerate}
                    regenerating={regenerating}
                    isPublic={isPublic}
                />
                <SummaryCard summary={review.summary} />
                <ScoreBreakdownCard breakdown={review.score_breakdown} />
                {review.evolution && (
                    <EvolutionCard evolution={review.evolution} sessionNumber={session.session_number_in_series} />
                )}
                <StrengthsImprovementsCard
                    strengths={review.strengths || []}
                    areas={review.areas_to_improve || []}
                />
                <TechniquesCard
                    used={review.techniques_used || []}
                    missed={review.techniques_missed || []}
                />
                <KeyMomentsCard moments={review.key_moments || []} onJump={jumpToTurn} />
                {review.soap_note && review.notes_method_used === "soap" ? (
                    <SoapCard soap={review.soap_note} />
                ) : (
                    <NoteStyleReflection method={review.notes_method_used} />
                )}
                <TranscriptAndNotesCard
                    session={session}
                    highlightTurn={highlightTurn}
                    transcriptRef={transcriptRef}
                    isPublic={isPublic}
                />
                {!isPublic && session.session_number_in_series < 8 && status === "complete" && (
                    <ContinueCard
                        session={session}
                        onContinue={handleContinue}
                        continuing={continuing}
                    />
                )}
                <FooterActions
                    onPracticeAgain={() => navigate("/practice/setup")}
                    isPublic={isPublic}
                />
                </div>
            </ReviewErrorBoundary>
        </Layout>
    );
}

/* ---------- Sections ---------- */

const HeaderCard = ({ session, review, onRegenerate, regenerating, isPublic }) => {
    return (
        <Card className="border-border/80 shadow-sm" data-testid="review-header-card">
            <CardContent className="grid gap-6 p-6 md:grid-cols-[160px_1fr_auto] md:items-center">
                <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-border md:h-32 md:w-32">
                        <AvatarPortrait
                            name={session.avatar_name}
                            imageUrl={session.avatar_image_url}
                            aspect={session.is_couple ? "wide" : "square"}
                            rounded="rounded-none"
                        />
                    </div>
                    <div className="md:mt-2">
                        <p className="font-semibold text-foreground" data-testid="review-avatar-name">{session.avatar_name}</p>
                        <p className="text-xs text-muted-foreground">
                            {session.is_couple ? "Couple" : `${session.age || ""}`}
                        </p>
                    </div>
                </div>

                <div className="space-y-2 text-sm">
                    {isPublic && session.counsellor_display_name && (
                        <div
                            className="mb-1 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-medium text-foreground"
                            data-testid="review-public-reviewer-label"
                        >
                            <User className="h-3 w-3" />
                            Reviewed by {session.counsellor_display_name}
                        </div>
                    )}
                    <MetaLine label="Therapy" value={session.therapy_type_name} testId="review-meta-therapy" />
                    <MetaLine label="Case" value={session.case_title} testId="review-meta-case" />
                    <MetaLine label="Duration" value={`${session.duration_minutes} minutes`} testId="review-meta-duration" />
                    {!isPublic && (
                        <MetaLine label="Counsellor" value={session.counsellor_display_name} testId="review-meta-counsellor" />
                    )}
                    {review.generated_at && (
                        <MetaLine label="Reviewed" value={new Date(review.generated_at).toLocaleString()} testId="review-meta-generated-at" />
                    )}
                </div>

                <div className="flex flex-col items-center gap-4 self-center">
                    <ScoreGauge score={review.overall_score ?? 0} />
                    {!isPublic && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="-mt-2" aria-label="More" data-testid="review-header-more-button">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={onRegenerate}
                                    disabled={regenerating}
                                    data-testid="review-header-regenerate"
                                >
                                    {regenerating ? (<><Loader2 className="h-4 w-4 animate-spin" />Regenerating…</>) : (<><RotateCcw className="h-4 w-4" />Regenerate review</>)}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const MetaLine = ({ label, value, testId }) => (
    <div className="grid grid-cols-[90px_1fr] gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="text-foreground" data-testid={testId}>{value}</span>
    </div>
);

const SummaryCard = ({ summary }) => (
    <Card className="border-border/80 shadow-sm" data-testid="review-summary-card">
        <CardContent className="p-6 space-y-2">
            <SectionLabel>Summary</SectionLabel>
            <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="review-summary-text">
                {summary || "—"}
            </p>
        </CardContent>
    </Card>
);

const StrengthsImprovementsCard = ({ strengths, areas }) => (
    <Card className="border-border/80 shadow-sm" data-testid="review-strengths-improvements-card">
        <CardContent className="grid gap-6 p-6 md:grid-cols-2">
            <div className="space-y-3">
                <SectionLabel className="text-emerald-700">Strengths</SectionLabel>
                <ul className="space-y-2" data-testid="review-strengths-list">
                    {strengths.map((s, i) => (
                        <li key={`strength-${i}-${(s || "").slice(0, 24)}`} className="flex items-start gap-2 text-sm leading-relaxed text-foreground" data-testid={`review-strength-${i}`}>
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                                <Check className="h-3 w-3" />
                            </span>
                            {s}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="space-y-3">
                <SectionLabel className="text-amber-700">Areas to improve</SectionLabel>
                <ul className="space-y-2" data-testid="review-areas-list">
                    {areas.map((s, i) => (
                        <li key={`area-${i}-${(s || "").slice(0, 24)}`} className="flex items-start gap-2 text-sm leading-relaxed text-foreground" data-testid={`review-area-${i}`}>
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                                <ArrowUpRight className="h-3 w-3" />
                            </span>
                            {s}
                        </li>
                    ))}
                </ul>
            </div>
        </CardContent>
    </Card>
);

const TechniquesCard = ({ used, missed }) => (
    <Card className="border-border/80 shadow-sm" data-testid="review-techniques-card">
        <CardContent className="p-6 space-y-5">
            <div className="space-y-2">
                <SectionLabel>Techniques used</SectionLabel>
                <div className="flex flex-wrap gap-2" data-testid="review-techniques-used">
                    {used.length === 0 && <span className="text-xs text-muted-foreground">None observed</span>}
                    {used.map((t, i) => (
                        <span key={`tu-${i}-${t}`} className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary" data-testid={`review-technique-used-${i}`}>
                            {t}
                        </span>
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <SectionLabel>Techniques missed</SectionLabel>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-2" data-testid="review-techniques-missed">
                    {missed.length === 0 && <span className="text-xs text-muted-foreground">None noted</span>}
                    {missed.map((t, i) => {
                        const brushUp = lookupBrushUp(t);
                        return (
                            <span
                                key={`tm-${i}-${t}`}
                                className="inline-flex items-center gap-1.5"
                                data-testid={`review-technique-missed-${i}-row`}
                            >
                                <span
                                    className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground"
                                    data-testid={`review-technique-missed-${i}`}
                                >
                                    {t}
                                </span>
                                {brushUp && (
                                    <Link
                                        to={`/learn/chapter/${brushUp.slug}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
                                        data-testid={`review-technique-missed-${i}-brushup`}
                                        title={`Open ${brushUp.title} in Learn`}
                                    >
                                        <BookOpen className="h-3 w-3" />
                                        Brush up: {brushUp.title}
                                    </Link>
                                )}
                            </span>
                        );
                    })}
                </div>
            </div>
        </CardContent>
    </Card>
);

const KeyMomentsCard = ({ moments, onJump }) => (
    <Card className="border-border/80 shadow-sm" data-testid="review-key-moments-card">
        <CardContent className="p-6 space-y-3">
            <SectionLabel>Key moments</SectionLabel>
            <ul className="space-y-3" data-testid="review-key-moments-list">
                {moments.length === 0 && <li className="text-sm text-muted-foreground">No key moments flagged.</li>}
                {moments.map((m, i) => (
                    <li key={`km-${m.turn_index}-${i}`} className="flex items-start gap-3 rounded-xl border border-border/80 bg-surface p-3" data-testid={`review-key-moment-${i}`}>
                        <span className="inline-flex shrink-0 items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                            Turn {m.turn_index}
                        </span>
                        <p className="flex-1 text-sm leading-relaxed text-foreground">{m.observation}</p>
                        <button
                            onClick={() => onJump(m.turn_index)}
                            className="text-xs font-medium text-primary hover:underline whitespace-nowrap"
                            data-testid={`review-key-moment-${i}-jump`}
                        >
                            → Jump to transcript
                        </button>
                    </li>
                ))}
            </ul>
        </CardContent>
    </Card>
);

const SoapCard = ({ soap }) => (
    <Card className="border-border/80 shadow-sm" data-testid="review-soap-card">
        <CardContent className="p-6 space-y-4">
            <SectionLabel>SOAP note</SectionLabel>
            <div className="space-y-3">
                {[
                    { key: "subjective", title: "S — Subjective" },
                    { key: "objective", title: "O — Objective" },
                    { key: "assessment", title: "A — Assessment" },
                    { key: "plan", title: "P — Plan" },
                ].map(({ key, title }) => (
                    <div key={key} className="rounded-xl border border-border/80 bg-surface p-4" data-testid={`review-soap-${key}`}>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
                        <p className="font-serif-display text-base leading-relaxed text-foreground whitespace-pre-wrap">
                            {soap[key] || "—"}
                        </p>
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

// Phase 12 — softer banner shown when the student used a non-SOAP note
// style (narrative / bullets / mixed). Skips render entirely when no notes
// were taken or the field is missing/legacy.
const NOTE_STYLE_LABELS = {
    narrative: "narrative",
    bullets: "bulleted",
    mixed: "mixed",
};

const NoteStyleReflection = ({ method }) => {
    const label = NOTE_STYLE_LABELS[method];
    if (!label) return null;
    return (
        <Card className="border-border/80 shadow-sm" data-testid="review-note-style-card">
            <CardContent className="p-6 space-y-2">
                <SectionLabel>Clinical note reflection</SectionLabel>
                <p className="text-sm italic text-muted-foreground" data-testid="review-note-style-line">
                    You used a {label} note style — assessed accordingly.
                </p>
            </CardContent>
        </Card>
    );
};

const TranscriptAndNotesCard = ({ session, highlightTurn, transcriptRef, isPublic }) => {
    const [transcriptOpen, setTranscriptOpen] = useState(true);
    const [notesOpen, setNotesOpen] = useState(true);
    const conversation = useMemo(
        () => (session.conversation || []).map((t, i) => ({ ...t, _key: `${i}-${t.role}` })),
        [session.conversation]
    );
    return (
        <Card className="border-border/80 shadow-sm" data-testid="review-transcript-notes-card">
            <CardContent className={cn("grid gap-6 p-6", isPublic ? "md:grid-cols-1" : "md:grid-cols-[1.5fr_1fr]")}>
                <section className="space-y-3" ref={transcriptRef}>
                    <CollapsibleHeader
                        label="Transcript"
                        open={transcriptOpen}
                        onToggle={() => setTranscriptOpen((v) => !v)}
                        testId="review-transcript-toggle"
                    />
                    {transcriptOpen && (
                        <div className="flex max-h-[600px] flex-col gap-4 overflow-y-auto rounded-xl bg-secondary/40 p-4" data-testid="review-transcript">
                            {conversation.length === 0 && (
                                <p className="text-sm text-muted-foreground">No turns recorded.</p>
                            )}
                            {conversation.map((t, idx) => (
                                <div
                                    key={t._key}
                                    data-turn-idx={idx}
                                    className={cn(
                                        "rounded-xl transition-all",
                                        highlightTurn === idx && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                                    )}
                                >
                                    <div className="mb-1 px-2 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
                                        Turn {idx}
                                    </div>
                                    <ChatMessage
                                        turn={t}
                                        avatarName={session.avatar_name}
                                        avatarImageUrl={session.avatar_image_url}
                                        counsellorName={session.counsellor_display_name}
                                        isCouple={session.is_couple}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </section>
                {!isPublic && (
                    <section className="space-y-3">
                        <CollapsibleHeader
                            label="Your notes"
                            open={notesOpen}
                            onToggle={() => setNotesOpen((v) => !v)}
                            testId="review-notes-toggle"
                        />
                        {notesOpen && (
                            <div className="rounded-xl border border-border/80 bg-surface p-4 text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="review-notes">
                                {session.student_notes && session.student_notes.trim()
                                    ? session.student_notes
                                    : <span className="text-muted-foreground">No notes were taken during this session.</span>}
                            </div>
                        )}
                    </section>
                )}
            </CardContent>
        </Card>
    );
};

const CollapsibleHeader = ({ label, open, onToggle, testId }) => (
    <button
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left hover:bg-secondary/50"
        data-testid={testId}
    >
        <SectionLabel>{label}</SectionLabel>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
    </button>
);

const SectionLabel = ({ children, className }) => (
    <h2 className={cn("text-xs font-semibold uppercase tracking-wider text-muted-foreground", className)}>
        {children}
    </h2>
);

const TRAJECTORY_TONE = {
    improved: "border-emerald-200 bg-emerald-50 text-emerald-700",
    plateaued: "border-amber-200 bg-amber-50 text-amber-800",
    regressed: "border-rose-200 bg-rose-50 text-rose-700",
    mixed: "border-sky-200 bg-sky-50 text-sky-700",
};

const EvolutionCard = ({ evolution, sessionNumber }) => {
    const tone = TRAJECTORY_TONE[evolution.trajectory] || "border-primary/30 bg-primary/10 text-primary";
    return (
        <Card className="border-border/80 shadow-sm" data-testid="review-evolution-card">
            <CardContent className="p-6 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <SectionLabel>
                        <span className="inline-flex items-center gap-1.5">
                            <TrendingUp className="h-3 w-3" />
                            Arc across the series
                        </span>
                    </SectionLabel>
                    <span
                        className={cn(
                            "rounded-full border px-3 py-0.5 text-xs font-medium capitalize",
                            tone
                        )}
                        data-testid="review-evolution-trajectory"
                    >
                        {evolution.trajectory}
                    </span>
                </div>
                <p
                    className="text-sm leading-relaxed text-foreground whitespace-pre-wrap"
                    data-testid="review-evolution-observation"
                >
                    {evolution.observation}
                </p>
                <p className="text-xs text-muted-foreground">
                    Comparing against the prior {sessionNumber - 1} session{sessionNumber - 1 === 1 ? "" : "s"} in this series.
                </p>
            </CardContent>
        </Card>
    );
};

const ContinueCard = ({ session, onContinue, continuing }) => {
    const nextNumber = session.session_number_in_series + 1;
    return (
        <Card className="border-primary/30 bg-primary/[0.04] shadow-sm" data-testid="review-continue-card">
            <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                        <Layers className="h-4 w-4" />
                    </span>
                    <div className="space-y-1">
                        <h3 className="font-semibold text-foreground" data-testid="review-continue-title">
                            Continue with {session.avatar_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Carry this work forward. Your next session will pick up where you left off — same client, same case, with this session&apos;s continuity note in the room.
                        </p>
                        <p className="text-xs font-medium text-primary" data-testid="review-continue-counter">
                            This will be session {nextNumber} of 8.
                        </p>
                    </div>
                </div>
                <Button
                    size="lg"
                    onClick={onContinue}
                    disabled={continuing}
                    className="bg-primary hover:bg-primary-hover shrink-0"
                    data-testid="review-continue-button"
                >
                    {continuing ? (<><Loader2 className="h-4 w-4 animate-spin" />Starting…</>) : (<>Continue series<ArrowRight className="h-4 w-4" /></>)}
                </Button>
            </CardContent>
        </Card>
    );
};

const FooterActions = ({ onPracticeAgain, isPublic }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
        <Button
            asChild
            variant="outline"
            data-testid="review-back-home-button"
        >
            <Link to={isPublic ? "/all-sessions" : "/"}>
                <ArrowLeft className="h-4 w-4" />
                {isPublic ? "Back to all sessions" : "Back to home"}
            </Link>
        </Button>
        <Button
            onClick={onPracticeAgain}
            className="bg-primary hover:bg-primary-hover"
            data-testid={isPublic ? "review-try-this-case-button" : "review-practice-again-button"}
        >
            {isPublic ? "Try this case yourself" : "Practice again"}
            <ArrowRight className="h-4 w-4" />
        </Button>
    </div>
);
